// Layer 3 (Transformations / seed data): pure data, no React. The canonical
// demo project records — the portfolio table, sidebar module nav, TopBar
// switcher, and Home cards all derive from this single list.
//
// parity.P5: mirrors ai-foundation's ProjectTile + ProjectTileCounts shape
// (src/app/(app)/projects/ProjectsGrid.tsx) with the mock's slug routing.

import type { ProjectColor } from './project-colors';
import { SEED_THREADS } from './thread-seed';

export type ProjectTile = {
  slug: string;
  name: string;
  description: string;
  color: ProjectColor;
  /** ms epoch */
  createdAt: number;
  /** ms epoch */
  updatedAt: number;
  /** Chats in the project — derived from the thread seed (`threadCountFor`). */
  threadCount: number;
  /** Seeded file count. Absent on the lead-zinc concentrator, whose files are
   *  its ingestion artefacts, counted by `projectFileCount`
   *  (`modules/projects/ProjectFiles.transform.ts`). */
  fileCount?: number;
  /** ms epoch of last chat/file touch; null = never */
  lastActivityAt: number | null;
  /** Portfolio "Owner" column — 'owner' renders as "You". */
  viewerRole: 'owner' | 'member' | 'viewer';
};

/** A project's chats are the seeded threads that name it. */
function threadCountFor(slug: string): number {
  return SEED_THREADS.filter((thread) => thread.projectSlug === slug).length;
}

const now = Date.now();
const hour = 60 * 60 * 1000;
const day = 24 * hour;

export const PROJECT_TILES: ProjectTile[] = [
  {
    slug: 'alpha',
    name: 'Lead-zinc concentrator',
    description: 'Zn-Pb-Ag concentrator · 1,800 stpd — live OptiFlow mock seed.',
    color: 'crimson',
    createdAt: now - 42 * day,
    updatedAt: now - 2 * hour,
    threadCount: threadCountFor('alpha'),
    lastActivityAt: now - 2 * hour,
    viewerRole: 'owner',
  },
  {
    slug: 'beta',
    name: 'Project Beta',
    description: 'Second demo project — proves the project switcher.',
    color: 'moss',
    createdAt: now - 30 * day,
    updatedAt: now - 3 * day,
    threadCount: threadCountFor('beta'),
    fileCount: 4,
    lastActivityAt: now - 3 * day,
    viewerRole: 'owner',
  },
  {
    slug: 'gamma',
    name: 'Field Study Gamma',
    description: 'Shared demo project — exercises the portfolio "Shared" pill.',
    color: 'ochre',
    createdAt: now - 12 * day,
    updatedAt: now - 26 * hour,
    threadCount: threadCountFor('gamma'),
    fileCount: 7,
    lastActivityAt: now - 26 * hour,
    viewerRole: 'member',
  },
  {
    slug: 'delta',
    name: 'Archive Delta',
    description: 'Dormant demo project — no recent activity.',
    color: 'slate',
    createdAt: now - 90 * day,
    updatedAt: now - 21 * day,
    threadCount: threadCountFor('delta'),
    fileCount: 2,
    lastActivityAt: null,
    viewerRole: 'owner',
  },
];

export function projectTileBySlug(slug: string | undefined): ProjectTile | null {
  return PROJECT_TILES.find((p) => p.slug === slug) ?? null;
}

/** Sentence form of the demo plant name — article + lower-case c. Chrome labels keep `ProjectTile.name`. */
export function plantPhrase(name: string): string {
  if (name === 'Lead-zinc concentrator') return 'the lead-zinc concentrator';
  return name;
}
